import { Children, useMemo } from 'react';
import { TooltipWrapper } from '../../display/TooltipWrapper';
import type { Props as AvatarProps } from './Avatar';
import styles from './AvatarGroup.module.scss';

export type Props = {
  /**
   * Array of `children` to render, typically `Avatar`s.
   * This prop is specifically typed to allow for `map`ing data
   * to `Element`s like `Avatar` and counting the rest.
   */
  children: React.ReactNode;
  /**
   * Controls the maximum number of `children` to render before
   * rendering a counter for the remaining children.
   */
  max?: number;
  /**
   * The `size` of the `Avatar`s that render within the group.
   */
  size?: AvatarProps['size'];
  /**
   * A tooltip to show for the overflow count, to override the default copy.
   */
  overflowCopy?: string;
};

/**
 * Displays a group of `Avatar` components.
 */
export const AvatarGroup = ({
  children,
  size = undefined,
  max = 3,
  overflowCopy,
}: Props) => {
  const childrenArray = Children.toArray(children).filter(Boolean);
  const slicedChildren = childrenArray.slice(0, max);
  const remainingChildren = childrenArray.length - slicedChildren.length;
  const overflowTooltip = useMemo(
    () => overflowCopy || `Plus ${remainingChildren} others`,
    [overflowCopy, remainingChildren]
  );
  const content =
    size !== 'large' ? (
      <div className={styles.count} data-zds>
        {overflowCopy || `${Math.min(remainingChildren, 999)} others`}
      </div>
    ) : (
      <TooltipWrapper content={overflowTooltip} position="southeast">
        {() => (
          <div aria-label={overflowTooltip} className={styles.count} data-zds>
            {Math.min(remainingChildren, 999)}
          </div>
        )}
      </TooltipWrapper>
    );
  return (
    <div className={styles.avatarGroup} data-size={size} data-zds>
      {slicedChildren}
      {remainingChildren > 0 && content}
    </div>
  );
};
